import React, { useState, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { FAIL, initStateObject, objectReducer } from '../../reducers/default'
import { checkIfPasswordIsSecureEnough } from 'utils/helpers'
import LoginSuccess from './LoginSuccess'
import { useCognito, useCognitoDispatch } from './CognitoContextProvider'
import { usePostLoginRedirect } from './hooks'
import InactiveUserModal from './Shared/InactiveUserModal'
import { challengeTypes } from './types'

const initialState = {
  email: '',
  password: '',
  newPassword: '',
  confirmPassword: '',
}

/**
 * Login form for the application (connected to Cognito AWS in the Backend).
 * @Tags( cognito, api )
 * @Inputs( email, password )
 * @Endpoints( /auth, /features )
 * @ApiLogic( ../../api/auth )
 */
const Form = ({ state, formStatus, handleSubmit, t, handleChange }) => {
  const { newPassword, confirmPassword } = state
  const { error, loading } = useCognito()

  return (
    <>
      <form onSubmit={handleSubmit}>
        {error && !error.includes('error.auth.inactive') && (
          <p data-testid="errorForm" className="alert alert-danger">
            {t(error)}
          </p>
        )}
        {formStatus.error && formStatus.message && formStatus.message !== 'error.auth.inactive' && (
          <p data-testid="errorForm" className="alert alert-danger">
            {t(formStatus.message)}
          </p>
        )}
        <div className="form-group">
          <input
            data-cy="password-input-cy"
            type="password"
            className="form-control s-50"
            id="newPassword"
            value={newPassword}
            onChange={(e) => handleChange(e)}
            placeholder={t('common.password')}
          />
        </div>
        <div className="form-group">
          <input
            data-cy="confirm-password-input-cy"
            type="password"
            className="form-control s-50"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => handleChange(e)}
            placeholder={t('common.confirmPassword')}
          />
        </div>
        <button
          data-cy="set-password-submit-button-cy"
          type="submit"
          className="bt bt-navy s-50"
          style={{ width: '100%' }}
          disabled={loading}
        >
          {loading ? t('SetPassword.form.btnLoading') : t('SetPassword.form.btn')}
        </button>
      </form>
      <InactiveUserModal />
    </>
  )
}

Form.propTypes = {
  state: PropTypes.object,
  formStatus: PropTypes.object,
  handleSubmit: PropTypes.func,
  t: PropTypes.func,
  handleChange: PropTypes.func,
}

const SetPasswordForm = ({ onSuccess }) => {
  const { t } = useTranslation()
  const [state, setState] = useState(initialState)
  const user = useSelector((state) => state.user)
  const { loginState, showSplash, setShowSplash } = usePostLoginRedirect(onSuccess)
  const [formStatus, setFormStatus] = useReducer(objectReducer, initStateObject)
  const { handleChangePasswordChallenge } = useCognitoDispatch()
  const { challenge } = useCognito()
  const { newPassword, confirmPassword } = state
  const [success, setSuccess] = useState(false)

  const handleChange = (event) => {
    setState({ ...state, [event.target.id]: event.target.value })
  }

  const validateForm = () => {
    if (newPassword.length === 0) {
      setFormStatus({ type: FAIL, payload: t('common.errors.fillRequired') })
      return false
    } else if (newPassword !== confirmPassword) {
      setFormStatus({ type: FAIL, payload: t('common.errors.wrongConfirmPassword') })
      return false
    }

    const isPasswordSecureEnough = checkIfPasswordIsSecureEnough(newPassword)
    if (!isPasswordSecureEnough.status) {
      setFormStatus({ type: FAIL, payload: t(isPasswordSecureEnough.message) })
      return false
    }

    return true
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    handleChangePasswordChallenge({ newPassword }, ({ success }) => {
      setSuccess(success)
    })
  }

  if (success) {
    return <Redirect to="/setup-mfa" />
  }

  if (
    challenge &&
    [challengeTypes.SOFTWARE_TOKEN_MFA, challengeTypes.EMAIL_OTP].includes(challenge.name)
  ) {
    return <Redirect to="/mfa" />
  }

  return user.isLogged ? (
    <LoginSuccess state={loginState} showSplash={showSplash} setShowSplash={setShowSplash} />
  ) : (
    <Form
      state={state}
      formStatus={formStatus}
      handleSubmit={handleSubmit}
      t={t}
      handleChange={handleChange}
    />
  )
}

SetPasswordForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
}

export default SetPasswordForm
